import { useCallback, useEffect } from "react";

/**
 * Lazy-load-on-scroll for the Table: watches the relevant scroll container (the table's own
 * viewport in sticky mode, the outlet otherwise) and fetches the next page once the last body row
 * scrolls into view. Extracted from Table.tsx (file-length ratchet).
 */
export const useInfiniteScroll = ({
	handleFetchMore,
	isFetching,
	currentPage,
	totalPages,
	page,
	sticky,
}: {
	handleFetchMore?: () => void;
	isFetching: boolean;
	currentPage: number;
	totalPages: number;
	page?: number;
	sticky: boolean;
}): void => {
	const loadMoreData = useCallback(() => {
		if (handleFetchMore && !isFetching && currentPage < totalPages) {
			handleFetchMore();
		}
	}, [currentPage, handleFetchMore, isFetching, totalPages]);

	useEffect(() => {
		const tableBlock = document.querySelector("#table-block");
		const outletContainer = document.querySelector("#outlet-container");
		const scrollContainer = sticky ? tableBlock : outletContainer;

		const isLastElementVisible = () => {
			const lastRow = (sticky ? tableBlock : outletContainer)?.querySelector(
				"tbody tr:last-of-type"
			);
			const rect = lastRow?.getBoundingClientRect();

			if (rect) {
				const isVisible =
					rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

				if (isVisible) {
					loadMoreData();
				}
			}
		};

		const handleScroll = () => {
			isLastElementVisible();
		};

		scrollContainer?.addEventListener("scroll", handleScroll);

		isLastElementVisible();

		return () => {
			scrollContainer?.removeEventListener("scroll", handleScroll);
		};
	}, [loadMoreData, page, sticky]);
};
